import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { adminAPI, customerAPI } from '../services/api';
import { LuChevronDown, LuChevronUp, LuFolderOpen, LuEye, LuX, LuPlus } from 'react-icons/lu';
import LoadingSpinner from './LoadingSpinner';
import BulkIdentifierInput from './BulkIdentifierInput';
import { toast } from 'react-toastify';

const InventoryAssetsTab = ({ inventoryItemId, isCustomer = false }) => {
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showSerialDetail, setShowSerialDetail] = useState(null);
  const [assignForm, setAssignForm] = useState({ project_name: '', project_code: '', selectedAssetIds: [], assetType: '' });
  const [expandedSections, setExpandedSections] = useState(new Set(['projects']));
  const [assignStep, setAssignStep] = useState('type'); // 'type' | 'select' | 'confirm'
  const [assignSearch, setAssignSearch] = useState('');
  const [showAddIdentifiers, setShowAddIdentifiers] = useState(false);
  const [addIdType, setAddIdType] = useState('serial'); // 'serial' | 'mac'
  const [addIdValues, setAddIdValues] = useState([]);

  const { data, isLoading, refetch } = useQuery(
    ['inventory-assets', inventoryItemId],
    () => (
      isCustomer
        ? customerAPI.getInventoryAssets(inventoryItemId, { limit: 5000 })
        : adminAPI.getInventoryAssets(inventoryItemId, { limit: 5000 })
    ),
    { enabled: !!inventoryItemId, staleTime: 30000 }
  );

  const assets = data?.data || [];
  const serialNumbers = assets.filter(a => a.serial_number).map(a => ({
    id: a.id, sn: a.serial_number, status: a.status, project: a.project_code
  }));
  const macAddresses = assets.filter(a => a.mac_address).map(a => ({
    id: a.id, mac: a.mac_address, status: a.status, project: a.project_code
  }));

  // Group assets by project
  const projectGroups = {};
  assets.forEach(a => {
    const key = a.project_code || a.project_name || '__unassigned__';
    if (!projectGroups[key]) {
      projectGroups[key] = {
        project_name: a.project_name || null,
        project_code: a.project_code || null,
        assets: []
      };
    }
    projectGroups[key].assets.push(a);
  });

  const unassigned = projectGroups['__unassigned__']?.assets || [];
  const assignedProjects = Object.entries(projectGroups).filter(([k]) => k !== '__unassigned__');

  const toggleSection = (section) => {
    const next = new Set(expandedSections);
    if (next.has(section)) next.delete(section); else next.add(section);
    setExpandedSections(next);
  };

  const handleAssign = async () => {
    if (assignForm.selectedAssetIds.length === 0) { toast.error('Select assets to assign'); return; }
    if (!assignForm.project_name && !assignForm.project_code) { toast.error('Enter a project name or code'); return; }
    try {
      await adminAPI.updateAssetProject({
        assetIds: assignForm.selectedAssetIds,
        project_name: assignForm.project_name || null,
        project_code: assignForm.project_code || null
      });
      toast.success(`Assigned ${assignForm.selectedAssetIds.length} asset(s) to project`);
      setShowAssignModal(false);
      setAssignForm({ project_name: '', project_code: '', selectedAssetIds: [] });
      setAssignStep('select');
      refetch();
    } catch (err) {
      toast.error('Failed to assign');
    }
  };

  const toggleAssetSelection = (id) => {
    setAssignForm(prev => ({
      ...prev,
      selectedAssetIds: prev.selectedAssetIds.includes(id)
        ? prev.selectedAssetIds.filter(x => x !== id)
        : [...prev.selectedAssetIds, id]
    }));
  };

  const handleAddIdentifiers = async () => {
    if (addIdValues.length === 0) { toast.error('Enter at least one value'); return; }

    // Find assets without identifiers of the given type
    const targetAssets = addIdType === 'serial'
      ? assets.filter(a => !a.serial_number)
      : assets.filter(a => !a.mac_address);

    if (targetAssets.length === 0) {
      toast.error(`No assets without ${addIdType === 'serial' ? 'serial numbers' : 'MAC addresses'} to assign to`);
      return;
    }

    // Assign values to assets in order (first value to first unassigned asset, etc.)
    const assignments = addIdValues.slice(0, targetAssets.length).map((val, i) => ({
      assetId: targetAssets[i].id,
      ...(addIdType === 'serial' ? { serial_number: val } : { mac_address: val })
    }));

    try {
      await adminAPI.updateAssetIdentifiers({ assignments });
      toast.success(`Assigned ${assignments.length} ${addIdType === 'serial' ? 'serial number(s)' : 'MAC address(es)'}`);
      if (addIdValues.length > targetAssets.length) {
        toast.info(`${addIdValues.length - targetAssets.length} value(s) had no matching unassigned assets`);
      }
      setShowAddIdentifiers(false);
      setAddIdValues([]);
      refetch();
    } catch (err) {
      toast.error('Failed to assign identifiers');
    }
  };

  // Remove a single asset from its project
  const handleRemoveFromProject = async (assetId) => {
    try {
      await adminAPI.updateAssetProject({
        assetIds: [assetId],
        project_name: null,
        project_code: null
      });
      toast.success('Removed from project');
      refetch();
    } catch (err) {
      toast.error('Failed to remove');
    }
  };

  // Delete an entire project (clear project from all assets in the group)
  const handleDeleteProject = async (groupKey) => {
    const group = projectGroups[groupKey];
    if (!group) return;
    if (!window.confirm(`Remove all ${group.assets.length} asset(s) from project "${group.project_name || group.project_code}"?`)) return;

    try {
      await adminAPI.updateAssetProject({
        assetIds: group.assets.map(a => a.id),
        project_name: null,
        project_code: null
      });
      toast.success(`Project cleared from ${group.assets.length} asset(s)`);
      setShowSerialDetail(null);
      refetch();
    } catch (err) {
      toast.error('Failed to delete project');
    }
  };

  const selectAllUnassigned = () => {
    setAssignForm(prev => ({
      ...prev,
      selectedAssetIds: unassigned.map(a => a.id)
    }));
  };

  if (isLoading) return <div className="py-8 flex justify-center"><LoadingSpinner /></div>;

  const SectionHeader = ({ title, section, count }) => (
    <button
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors"
    >
      <div className="flex items-center space-x-2">
        {expandedSections.has(section) ? <LuChevronUp className="h-4 w-4 text-gray-400" /> : <LuChevronDown className="h-4 w-4 text-gray-400" />}
        <span className="text-sm font-medium text-gray-900">{title}</span>
        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{count}</span>
      </div>
    </button>
  );

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg">
      <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
        <div>
          <h3 className="text-lg leading-6 font-medium text-gray-900">Assets & Projects</h3>
          <p className="mt-1 text-sm text-gray-500">{assets.length} asset(s) in stock</p>
        </div>
        {!isCustomer && (
          <div className="flex space-x-2">
            <button
              onClick={() => { setAddIdType('serial'); setAddIdValues([]); setShowAddIdentifiers(true); }}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <LuPlus className="mr-1.5 h-4 w-4" />
              Add Serials
            </button>
            <button
              onClick={() => { setAddIdType('mac'); setAddIdValues([]); setShowAddIdentifiers(true); }}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <LuPlus className="mr-1.5 h-4 w-4" />
              Add MACs
            </button>
            <button
              onClick={() => { setShowAssignModal(true); setAssignStep('type'); setAssignForm({ project_name: '', project_code: '', selectedAssetIds: [], assetType: '' }); setAssignSearch(''); }}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <LuFolderOpen className="mr-1.5 h-4 w-4" />
              Assign to Project
            </button>
          </div>
        )}
      </div>

      <div className="border-t border-gray-200 divide-y divide-gray-200">
        {/* Serial Numbers Section */}
        {serialNumbers.length > 0 && (
          <div>
            <SectionHeader title="Serial Numbers" section="serials" count={serialNumbers.length} />
            {expandedSections.has('serials') && (
              <div className="px-6 pb-4">
                <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
                  {serialNumbers.map((sn, i) => (
                    <span key={i} className={`inline-flex items-center px-2 py-1 rounded text-xs font-mono ${
                      sn.project ? 'bg-indigo-50 text-indigo-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {sn.sn}
                      {sn.project && <span className="ml-1 text-indigo-400 text-[10px]">({sn.project})</span>}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* MAC Addresses Section */}
        {macAddresses.length > 0 && (
          <div>
            <SectionHeader title="MAC Addresses" section="macs" count={macAddresses.length} />
            {expandedSections.has('macs') && (
              <div className="px-6 pb-4">
                <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
                  {macAddresses.map((mac, i) => (
                    <span key={i} className={`inline-flex items-center px-2 py-1 rounded text-xs font-mono ${
                      mac.project ? 'bg-indigo-50 text-indigo-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {mac.mac}
                      {mac.project && <span className="ml-1 text-indigo-400 text-[10px]">({mac.project})</span>}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Project Assignments Section */}
        <div>
          <SectionHeader title="Project Assignments" section="projects" count={assignedProjects.length} />
          {expandedSections.has('projects') && (
            <div className="px-6 pb-4">
              {assignedProjects.length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center">No assets assigned to projects yet.</p>
              ) : (
                <div className="space-y-3">
                  {assignedProjects.map(([key, group]) => (
                    <div key={key} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-sm font-medium text-gray-900">{group.project_name || 'Unnamed Project'}</span>
                          {group.project_code && (
                            <span className="ml-2 text-xs text-gray-500 font-mono bg-gray-100 px-1.5 py-0.5 rounded">{group.project_code}</span>
                          )}
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className="text-sm text-gray-500">{group.assets.length} asset(s)</span>
                          <button
                            onClick={() => setShowSerialDetail(key)}
                            className="text-indigo-600 hover:text-indigo-800 text-sm inline-flex items-center"
                          >
                            <LuEye className="h-3.5 w-3.5 mr-1" />
                            View
                          </button>
                          {!isCustomer && (
                            <button
                              onClick={() => handleDeleteProject(key)}
                              className="text-red-500 hover:text-red-700 text-sm"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                      {/* Show first few serials inline */}
                      {group.assets.some(a => a.serial_number) && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {group.assets.filter(a => a.serial_number).slice(0, 5).map((a, i) => (
                            <span key={i} className="text-xs font-mono bg-gray-50 px-1.5 py-0.5 rounded text-gray-600">{a.serial_number}</span>
                          ))}
                          {group.assets.filter(a => a.serial_number).length > 5 && (
                            <button onClick={() => setShowSerialDetail(key)} className="text-xs text-indigo-600 hover:text-indigo-800">
                              +{group.assets.filter(a => a.serial_number).length - 5} more
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {unassigned.length > 0 && (
                <div className="mt-3 border border-dashed border-gray-300 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Unassigned assets</span>
                    <span className="text-sm text-gray-400">{unassigned.length} asset(s)</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Serial Detail Modal */}
      {showSerialDetail && projectGroups[showSerialDetail] && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowSerialDetail(null)}></div>
            <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {projectGroups[showSerialDetail].project_name || 'Project'} - {projectGroups[showSerialDetail].project_code || ''}
                </h3>
                <button onClick={() => setShowSerialDetail(null)} className="text-gray-400 hover:text-gray-600"><LuX className="h-5 w-5" /></button>
              </div>
              <div className="space-y-1">
                {projectGroups[showSerialDetail].assets.map((a, i) => (
                  <div key={i} className="flex items-center justify-between text-sm py-1.5 px-3 bg-gray-50 rounded group">
                    <div className="flex-1 min-w-0">
                      {a.serial_number && <span className="font-mono text-gray-900">SN: {a.serial_number}</span>}
                      {a.mac_address && <span className="font-mono text-gray-600 ml-2">MAC: {a.mac_address}</span>}
                      {!a.serial_number && !a.mac_address && <span className="text-gray-400">Asset {a.id.substring(0, 8)}</span>}
                    </div>
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      <span className="text-xs text-gray-400">{a.bin_location || ''}</span>
                      {!isCustomer && (
                        <button
                          onClick={() => handleRemoveFromProject(a.id)}
                          className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                          aria-label="Remove from project"
                        >
                          <LuX className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t border-gray-200 flex justify-between items-center">
                {!isCustomer ? (
                  <button
                    onClick={() => handleDeleteProject(showSerialDetail)}
                    className="text-sm text-red-500 hover:text-red-700"
                  >
                    Remove all from project
                  </button>
                ) : <span />}
                <span className="text-xs text-gray-400">{projectGroups[showSerialDetail].assets.length} asset(s)</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign to Project Modal */}
      {!isCustomer && showAssignModal && (() => {
        // Only show assets not already assigned to a project
        const unassignedAssets = assets.filter(a => !a.project_name && !a.project_code);
        const typeAssets = assignForm.assetType === 'serial'
          ? unassignedAssets.filter(a => a.serial_number)
          : assignForm.assetType === 'mac'
            ? unassignedAssets.filter(a => a.mac_address)
            : unassignedAssets;
        const searchLower = assignSearch.toLowerCase();
        const filteredAssets = searchLower
          ? typeAssets.filter(a =>
              (a.serial_number || '').toLowerCase().includes(searchLower) ||
              (a.mac_address || '').toLowerCase().includes(searchLower) ||
              (a.project_code || '').toLowerCase().includes(searchLower) ||
              (a.project_name || '').toLowerCase().includes(searchLower)
            )
          : typeAssets;

        return (
          <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
            <div className="flex items-center justify-center min-h-screen p-4">
              <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowAssignModal(false)}></div>
              <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[85vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Assign Inventory to Project</h3>
                  <button onClick={() => { setShowAssignModal(false); setAssignStep('type'); setAssignSearch(''); }} className="text-gray-400 hover:text-gray-600"><LuX className="h-5 w-5" /></button>
                </div>

                {/* Step 1: Choose asset type */}
                {assignStep === 'type' && (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-500">What would you like to assign? Only unassigned assets are shown.</p>
                    {(() => {
                      const unassignedSerials = assets.filter(a => a.serial_number && !a.project_name && !a.project_code);
                      const unassignedMacs = assets.filter(a => a.mac_address && !a.project_name && !a.project_code);
                      const unassignedOther = assets.filter(a => !a.serial_number && !a.mac_address && !a.project_name && !a.project_code);
                      return (
                        <>
                          {unassignedSerials.length > 0 && (
                            <button
                              onClick={() => { setAssignForm(f => ({ ...f, assetType: 'serial', selectedAssetIds: [] })); setAssignStep('select'); setAssignSearch(''); }}
                              className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                            >
                              <div className="text-sm font-medium text-gray-900">Serial Numbers</div>
                              <div className="text-xs text-gray-500">{unassignedSerials.length} unassigned serial number(s)</div>
                            </button>
                          )}
                          {unassignedMacs.length > 0 && (
                            <button
                              onClick={() => { setAssignForm(f => ({ ...f, assetType: 'mac', selectedAssetIds: [] })); setAssignStep('select'); setAssignSearch(''); }}
                              className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                            >
                              <div className="text-sm font-medium text-gray-900">MAC Addresses</div>
                              <div className="text-xs text-gray-500">{unassignedMacs.length} unassigned MAC address(es)</div>
                            </button>
                          )}
                          {unassignedOther.length > 0 && (
                            <button
                              onClick={() => { setAssignForm(f => ({ ...f, assetType: 'all', selectedAssetIds: [] })); setAssignStep('select'); setAssignSearch(''); }}
                              className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                            >
                              <div className="text-sm font-medium text-gray-900">Other Assets</div>
                              <div className="text-xs text-gray-500">{unassignedOther.length} unassigned asset(s)</div>
                            </button>
                          )}
                          {unassignedSerials.length === 0 && unassignedMacs.length === 0 && unassignedOther.length === 0 && (
                            <p className="text-sm text-gray-500 text-center py-4">All assets are already assigned to projects.</p>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Step 2: Search and select */}
                {assignStep === 'select' && (
                  <>
                    <div className="mb-3">
                      <input
                        type="text"
                        value={assignSearch}
                        onChange={(e) => setAssignSearch(e.target.value)}
                        placeholder={`Search ${assignForm.assetType === 'serial' ? 'serial numbers' : assignForm.assetType === 'mac' ? 'MAC addresses' : 'assets'}...`}
                        className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-500">{filteredAssets.length} shown, {assignForm.selectedAssetIds.length} selected</span>
                      <div className="flex space-x-3">
                        <button
                          onClick={() => setAssignForm(f => ({ ...f, selectedAssetIds: filteredAssets.map(a => a.id) }))}
                          className="text-xs text-indigo-600 hover:text-indigo-800"
                        >
                          Select all shown
                        </button>
                        {assignForm.selectedAssetIds.length > 0 && (
                          <button
                            onClick={() => setAssignForm(f => ({ ...f, selectedAssetIds: [] }))}
                            className="text-xs text-gray-500 hover:text-gray-700"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="border rounded-lg divide-y max-h-60 overflow-y-auto mb-4">
                      {filteredAssets.map(a => {
                        const label = assignForm.assetType === 'serial' ? a.serial_number
                          : assignForm.assetType === 'mac' ? a.mac_address
                          : (a.serial_number || a.mac_address || `Asset ${a.id.substring(0, 8)}`);
                        return (
                          <label key={a.id} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={assignForm.selectedAssetIds.includes(a.id)}
                              onChange={() => toggleAssetSelection(a.id)}
                              className="h-4 w-4 text-indigo-600 border-gray-300 rounded mr-3 flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0 flex items-center justify-between">
                              <span className="text-sm text-gray-900 font-mono truncate">{label}</span>
                              {a.project_code && (
                                <span className="ml-2 text-xs text-gray-400 flex-shrink-0">{a.project_code}</span>
                              )}
                            </div>
                          </label>
                        );
                      })}
                      {filteredAssets.length === 0 && (
                        <div className="px-4 py-6 text-center text-sm text-gray-500">No matching assets found</div>
                      )}
                    </div>

                    <div className="flex justify-between">
                      <button onClick={() => { setAssignStep('type'); setAssignSearch(''); }} className="text-sm text-gray-600 hover:text-gray-800">Back</button>
                      <button
                        onClick={() => setAssignStep('confirm')}
                        disabled={assignForm.selectedAssetIds.length === 0}
                        className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                      >
                        Next ({assignForm.selectedAssetIds.length} selected)
                      </button>
                    </div>
                  </>
                )}

                {/* Step 3: Enter project info */}
                {assignStep === 'confirm' && (
                  <>
                    <p className="text-sm text-gray-500 mb-4">Assigning {assignForm.selectedAssetIds.length} asset(s) to project:</p>
                    <div className="space-y-4 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Project Name</label>
                        <input
                          type="text"
                          value={assignForm.project_name}
                          onChange={(e) => setAssignForm(f => ({ ...f, project_name: e.target.value }))}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          placeholder="e.g., Bank of Canada"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Project Code</label>
                        <input
                          type="text"
                          value={assignForm.project_code}
                          onChange={(e) => setAssignForm(f => ({ ...f, project_code: e.target.value }))}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          placeholder="e.g., BOC12345"
                        />
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <button onClick={() => setAssignStep('select')} className="text-sm text-gray-600 hover:text-gray-800">Back</button>
                      <button
                        onClick={handleAssign}
                        className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                      >
                        Assign to Project
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Add Identifiers Modal */}
      {!isCustomer && showAddIdentifiers && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowAddIdentifiers(false)}></div>
            <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Add {addIdType === 'serial' ? 'Serial Numbers' : 'MAC Addresses'}
                </h3>
                <button onClick={() => setShowAddIdentifiers(false)} className="text-gray-400 hover:text-gray-600"><LuX className="h-5 w-5" /></button>
              </div>

              <p className="text-sm text-gray-500 mb-4">
                {addIdType === 'serial'
                  ? `Paste or upload serial numbers. They will be assigned to the ${assets.filter(a => !a.serial_number).length} asset(s) that don't have serial numbers yet.`
                  : `Paste or upload MAC addresses. They will be assigned to the ${assets.filter(a => !a.mac_address).length} asset(s) that don't have MAC addresses yet.`
                }
              </p>

              <BulkIdentifierInput
                label={addIdType === 'serial' ? 'Serial Numbers' : 'MAC Addresses'}
                values={addIdValues}
                onChange={setAddIdValues}
                maxCount={addIdType === 'serial' ? assets.filter(a => !a.serial_number).length : assets.filter(a => !a.mac_address).length}
                placeholder={addIdType === 'serial' ? 'Enter serial number' : 'Enter MAC address'}
              />

              <div className="mt-6 flex justify-end space-x-3">
                <button onClick={() => setShowAddIdentifiers(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  onClick={handleAddIdentifiers}
                  disabled={addIdValues.length === 0}
                  className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                >
                  Assign {addIdValues.length} {addIdType === 'serial' ? 'Serial(s)' : 'MAC(s)'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryAssetsTab;
